import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import DisplayNameModal from "@/components/DisplayNameModal";

// Eager-loaded: Landing page (first paint) + lightweight nav components
import LandingPage from "@/pages/LandingPage";
import { MobileBottomNav } from "@/components/MobileNavigation";
import ArtistNotifications from "@/components/ArtistNotifications";

// Lazy-loaded: every other page becomes its own JS chunk that downloads
// only when the user navigates to that route. Massive perceived-speed win:
// initial JS payload drops from ~5+ MB to ~500 KB on production builds.
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const GoogleCallback = lazy(() => import("@/pages/GoogleCallback"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ArtistDashboard = lazy(() => import("@/pages/ArtistDashboard"));
const ReviewerDashboard = lazy(() => import("@/pages/ReviewerDashboard"));
const DiscoverReviewers = lazy(() => import("@/pages/DiscoverReviewers"));
const ReviewerProfile = lazy(() => import("@/pages/ReviewerProfile"));
const QueuePage = lazy(() => import("@/pages/QueuePage"));
const ReplaceSongPage = lazy(() => import("@/pages/ReplaceSongPage"));
const LiveDirectory = lazy(() => import("@/pages/LiveDirectory"));
const OfflineReviewers = lazy(() => import("@/pages/OfflineReviewers"));
const ReviewSubmissionForm = lazy(() => import("@/pages/ReviewSubmissionForm"));
const StreamerOverlay = lazy(() => import("@/pages/StreamerOverlay"));
const StreamerOverlayMini = lazy(() => import("@/pages/StreamerOverlay").then(m => ({ default: m.StreamerOverlayMini })));
const IPhoneOverlay = lazy(() => import("@/pages/IPhoneOverlay"));
const VerticalOverlay = lazy(() => import("@/pages/VerticalOverlay"));
const MainOverlay = lazy(() => import("@/pages/MainOverlay"));
const MinimalOverlay = lazy(() => import("@/pages/MinimalOverlay"));
const BillboardOverlay = lazy(() => import("@/pages/BillboardOverlay"));
const ReviewBattleOverlay = lazy(() => import("@/pages/ReviewBattleOverlay"));
const PokemonBattleOverlay = lazy(() => import("@/pages/PokemonBattleOverlay"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const BoostSuccess = lazy(() => import("@/pages/BoostSuccess"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PlatformOverview = lazy(() => import("@/pages/PlatformOverview"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const DeployingPage = lazy(() => import("@/components/DeployingPage"));
const InviteLanding = lazy(() => import("@/pages/InviteLanding"));
const SubmitTrack = lazy(() => import("@/pages/SubmitTrack"));
const DonatePage = lazy(() => import("@/pages/DonatePage"));
const DonationSuccess = lazy(() => import("@/pages/DonationSuccess"));
const V2PaymentSuccess = lazy(() => import("@/pages/V2PaymentResult").then(m => ({ default: m.V2PaymentSuccess })));
const V2PaymentCancel = lazy(() => import("@/pages/V2PaymentResult").then(m => ({ default: m.V2PaymentCancel })));

// RUNTIME API URL DETECTION - Prevents hardcoded URL issues during deployment
// This function determines the API URL based on the current window location,
// making it impossible to deploy with the wrong backend URL
const getApiBaseUrl = () => {
  // Check if we're in a production domain (afterplay.live or emergent.host deployments)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Production domains should always use their own origin
    if (hostname === 'afterplay.live' || hostname.endsWith('.emergent.host')) {
      console.log('[API] Production/Deployment detected, using origin URL');
      return window.location.origin;
    }
  }
  
  // For development/preview, use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback to current origin
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  // SSR fallback
  return '';
};

const BACKEND_URL = getApiBaseUrl();
export const API = `${BACKEND_URL}/api`;

// Log for debugging
if (typeof window !== 'undefined') {
  console.log('[API] Backend URL:', BACKEND_URL);
  console.log('[API] Full API URL:', API);
}

// Attach the auth token as an Authorization header on every request.
// Tokens used to travel in the URL query string (?token=...), which leaks them
// into server/proxy access logs and browser history. The backend now reads the
// token from this header (with the old query param still accepted as a fallback).
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Same treatment for fetch(). ~46 calls use fetch instead of axios and would
// otherwise be the only ones still putting the token in the URL. Guarded to our
// own backend so we never attach the user's token to a third-party request.
const _nativeFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  try {
    const url = typeof input === "string" ? input : input?.url || "";
    const isOwnApi = url.startsWith(BACKEND_URL) || url.startsWith("/api");
    if (isOwnApi) {
      const token = localStorage.getItem("token");
      const headers = new Headers(init.headers || (typeof input === "object" ? input.headers : undefined));
      if (token && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
        return _nativeFetch(input, { ...init, headers });
      }
    }
  } catch (e) {
    // Auth plumbing must never break a request — fall through to plain fetch.
    console.warn("[Auth] fetch header injection skipped:", e);
  }
  return _nativeFetch(input, init);
};

// Setup axios interceptor for automatic logout on token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error is due to token expiration
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('invalid token')) {
        console.log('[Auth] Token expired or invalid, logging out...');
        // Clear auth data
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        
        // Notify the React auth context to clear user state.
        // The interceptor lives outside the component tree, so we bridge
        // via a custom DOM event that AuthProvider listens for.
        try { window.dispatchEvent(new Event("auth:force-logout")); } catch (_) { /* best-effort */ }
        
        // Check if user is on a submission page — don't redirect away, let them sign in there
        const currentPath = window.location.pathname;
        const isSubmitPage = currentPath.includes('/submit');
        
        if (isSubmitPage) {
          // On submit pages: DON'T redirect — the guest fallback handles it silently
          // (intentionally empty: no toast, no redirect)
        } else {
          // On other pages: redirect to home
          toast.error("Your session has expired. Please log in again.");
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage synchronously to avoid flash
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true); // Start with loading true
  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);

  // Refresh token on app load - keeps users logged in as long as they're active
  useEffect(() => {
    const refreshTokenOnLoad = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        console.log("[Auth] Refreshing token on app load...");
        const response = await axios.post(`${API}/auth/refresh-token`);
        
        if (response.data?.token) {
          // Got a fresh token - save it
          localStorage.setItem("token", response.data.token);
          
          // Update user data if provided
          if (response.data.user) {
            const updatedUser = {
              ...response.data.user,
              _lastRefreshed: Date.now()
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            setUser(updatedUser);
          }
          console.log("[Auth] Token refreshed successfully - 7 day clock reset");
        }
      } catch (error) {
        console.log("[Auth] Token refresh failed:", error.response?.data?.detail || error.message);
        // If the server says the token is expired/invalid (401), sync React
        // state with the already-cleared localStorage.  Without this, `user`
        // stays truthy (from the initial synchronous read) while the token is
        // gone, causing silent submit failures for returning users.
        if (error.response?.status === 401) {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    refreshTokenOnLoad();
  }, []); // Run once on mount

  // Listen for forced logouts from the axios interceptor (which runs outside
  // the React tree and can't call setUser directly).
  useEffect(() => {
    const handleForceLogout = () => {
      console.log("[Auth] Force-logout event received — clearing user state");
      setUser(null);
    };
    window.addEventListener("auth:force-logout", handleForceLogout);
    return () => window.removeEventListener("auth:force-logout", handleForceLogout);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    
    // Check if user needs to set display name (handle both boolean and string)
    const needsDisplayName = userData.needs_display_name === true || userData.needs_display_name === "true";
    console.log("[Auth] Login - needs_display_name:", userData.needs_display_name, "showing modal:", needsDisplayName);
    
    if (needsDisplayName) {
      setShowDisplayNameModal(true);
    }
  };

  const logout = () => {
    // Clear auth data first
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Force navigation to home page with a full refresh immediately
    // Don't call setUser(null) or other state changes - they can trigger re-renders
    // that may interfere with the navigation
    window.location.href = '/';
  };

  const handleDisplayNameSet = (displayName) => {
    const updatedUser = { ...user, display_name: displayName, needs_display_name: false };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
    setShowDisplayNameModal(false);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    
    try {
      // Refreshing user;
      const response = await fetch(`${API}/users/profile`);
      if (response.ok) {
        const userData = await response.json();
        // Use FRESH server data as source of truth (avoid stale closure values).
        // Merge fresh data with the prior token/auth fields if needed.
        const updatedUser = {
          ...userData,
          // Legacy alias: some components read `name`, ensure it tracks display_name
          name: userData.display_name || userData.name,
          display_name: userData.display_name || userData.name,
          // Force update by adding timestamp (helps memo/equality checks)
          _lastUpdated: Date.now()
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser); // This triggers all components using useAuth()
        // Also broadcast for any non-context listeners (e.g. nav badges)
        try {
          window.dispatchEvent(new CustomEvent("auth:user-updated", { detail: updatedUser }));
        } catch (_) { /* event dispatch is best-effort */ }
        return updatedUser;
      }
    } catch (error) {
      console.error("❌ Failed to refresh user data:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
      {children}
      <DisplayNameModal
        open={showDisplayNameModal}
        onComplete={handleDisplayNameSet}
        defaultName=""
        API={API}
      />
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-purple-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Redirect component for legacy /submit/:reviewerId route
const SubmitRedirect = () => {
  const { reviewerId } = useParams();
  return <Navigate to={`/artist/submit/${reviewerId}`} replace />;
};

// RoleSwitcherWrapper removed - admin profile switching disabled

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          {/* Main content area with bottom padding for mobile nav */}
          <div className="pb-16 md:pb-0">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-purple-900">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            }>
              <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/google-callback" element={<GoogleCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/discover" element={<DiscoverReviewers />} />
              <Route path="/live" element={<LiveDirectory />} />
              <Route path="/offline-reviewers" element={<OfflineReviewers />} />
              <Route path="/reviewer/:id" element={<ReviewerProfile />} />
              <Route path="/queue/:reviewerId" element={<QueuePage />} />
              <Route path="/replace/:submissionId" element={<ReplaceSongPage />} />
              <Route path="/donate/:reviewerId" element={<DonatePage />} />
              <Route path="/donation/success" element={<DonationSuccess />} />
              <Route path="/invite" element={<InviteLanding />} />
              <Route path="/:profileCode/submit" element={<SubmitTrack />} />
              <Route path="/:profileCode" element={<ReviewerProfile />} />
              <Route
                path="/artist/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["artist"]}>
                    <ArtistDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/artist/submit/:reviewerId"
                element={<SubmitTrack />}
              />
              <Route
                path="/reviewer/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["reviewer"]}>
                    <ReviewerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reviewer/review/:submissionId"
                element={
                  <ProtectedRoute allowedRoles={["reviewer"]}>
                    <ReviewSubmissionForm />
                  </ProtectedRoute>
                }
              />
              {/* Streamer Overlay Routes - No auth required for OBS */}
              <Route path="/overlay/:reviewerId" element={<StreamerOverlay />} />
              <Route path="/overlay/:reviewerId/mini" element={<StreamerOverlayMini />} />
              <Route path="/overlay/:reviewerId/iphone" element={<IPhoneOverlay />} />
              <Route path="/overlay/:reviewerId/vertical" element={<VerticalOverlay />} />
              <Route path="/overlay/:reviewerId/main" element={<MainOverlay />} />
              <Route path="/overlay/:reviewerId/minimal" element={<MinimalOverlay />} />
              <Route path="/overlay/:reviewerId/billboard" element={<BillboardOverlay />} />
              <Route path="/overlay/:reviewerId/reviewbattle" element={<ReviewBattleOverlay />} />
              {/* Old Pokémon path kept as an alias so existing OBS links don't break */}
              <Route path="/overlay/:reviewerId/pokemon" element={<ReviewBattleOverlay />} />
              {/* Standalone old-school Pokémon-battle overlay (opt-in URL; does not affect /pokemon alias) */}
              <Route path="/overlay/:reviewerId/pokemonbattle" element={<PokemonBattleOverlay />} />
              
              {/* V1 Payment Routes (Stripe) */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              
              {/* V2 Payment Routes (Stripe) */}
              <Route path="/v2/payment/success" element={<V2PaymentSuccess />} />
              <Route path="/v2/payment/cancel" element={<V2PaymentCancel />} />
              
              {/* Boost Success Route */}
              <Route path="/boost/success" element={<BoostSuccess />} />
              
              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              
              {/* Platform Overview - Admin Data Hub */}
              <Route
                path="/admin/overview"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <PlatformOverview />
                  </ProtectedRoute>
                }
              />
              
              {/* Support Route */}
              <Route
                path="/support"
                element={
                  <ProtectedRoute allowedRoles={["artist", "reviewer"]}>
                    <SupportPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Terms of Service - Public */}
              <Route path="/terms" element={<TermsOfService />} />

              {/* Privacy Policy - Public */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              
              {/* Test route for deploying page preview */}
              <Route path="/deploying-preview" element={<DeployingPage onRetry={() => window.location.reload()} />} />
              
              {/* Redirect /submit/:reviewerId to /artist/submit/:reviewerId for backward compatibility */}
              <Route path="/submit/:reviewerId" element={<SubmitRedirect />} />

              {/* Catch-all 404 — MUST stay last so it only matches unknown URLs */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
          
          {/* Mobile Bottom Navigation - Always visible on mobile */}
          <MobileBottomNav />
          

          {/* Real-time Artist Notifications */}
          <ArtistNotifications />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
